import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { supabase } from '@/lib/supabase/cliente'
import { mensajeDeError } from '@/lib/supabase/errores'
import type { CotizacionParaPartir, ReferenciaManoDeObra } from '@/lib/supabase/tipos-bd'

/**
 * Partir de una cotización anterior.
 *
 * En las 2.616 del histórico no hay dos con el mismo conjunto de productos,
 * así que duplicar no sirve. Pero los trabajos se parecen: lo útil es traer
 * una parecida y ajustar cantidades.
 */

export function useCotizacionesParaPartir(ordenServicioId: string | null) {
  const consulta = useQuery({
    queryKey: ['cotizaciones-para-partir', ordenServicioId],
    enabled: Boolean(ordenServicioId),
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<CotizacionParaPartir[]> => {
      const { data, error } = await supabase.rpc('fn_cotizaciones_para_partir', {
        p_orden_servicio_id: ordenServicioId!,
        p_limite: 8,
      })

      if (error) throw new Error(mensajeDeError(error))

      return (data ?? []) as unknown as CotizacionParaPartir[]
    },
  })

  return { anteriores: consulta.data ?? [], cargando: consulta.isLoading }
}

export function useTraerRenglones() {
  const clienteConsultas = useQueryClient()

  return useMutation({
    mutationFn: async ({
      cotizacionId,
      idAsiento,
    }: {
      cotizacionId: string
      idAsiento: number
    }): Promise<number> => {
      const { data, error } = await supabase.rpc('fn_traer_renglones_de', {
        p_cotizacion_id: cotizacionId,
        p_id_asiento: idAsiento,
        // Los precios se traen del catálogo de hoy: una cotización de hace
        // seis meses tiene precios de hace seis meses.
        p_precios_de_hoy: true,
      })

      if (error) throw new Error(mensajeDeError(error))

      return data as number
    },
    onSuccess: async (_dato, variables) => {
      await clienteConsultas.invalidateQueries({
        queryKey: ['borrador-renglones', variables.cotizacionId],
      })
    },
  })
}

/**
 * Contra qué se compara la mano de obra de esta cotización.
 *
 * No es un objetivo que alguien fijó: es dónde caen las cotizaciones que la
 * empresa ya hizo. La base decide si son las de este servicio o las de todas.
 */
export function useReferenciaManoDeObra(ordenServicioId: string | null) {
  const consulta = useQuery({
    queryKey: ['referencia-mano-de-obra', ordenServicioId],
    enabled: Boolean(ordenServicioId),
    staleTime: 30 * 60 * 1000,
    queryFn: async (): Promise<ReferenciaManoDeObra | null> => {
      const { data, error } = await supabase.rpc('fn_referencia_mano_obra', {
        p_orden_servicio_id: ordenServicioId!,
      })

      if (error) throw new Error(mensajeDeError(error))

      const fila = (data as unknown as ReferenciaManoDeObra[] | null)?.[0]
      if (!fila) return null

      return {
        ...fila,
        minimo: Number(fila.minimo),
        mediana: Number(fila.mediana),
        maximo: Number(fila.maximo),
      }
    },
  })

  return { referencia: consulta.data ?? null }
}
